import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Flex,
  Text,
  Box,
  Badge,
  Avatar,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Textarea,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import '../App.css';
import { FaExchangeAlt, FaEdit, FaComments, FaTrash } from 'react-icons/fa';
import { useModernToast } from './ModernToast';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { getTeamName, getTeamLogo, getTeamInfoById } from '../utils/getTeamInfo';
import { getExpiryCountdown } from '../lib/pstTime';
import TeamLogo from './TeamLogo';
import PlayerSelectionModal from './PlayerSelectionModal';
import { displayTimeStamp } from '../lib/pstTime';
import SelectTeam from './SelectTeam';
import { useUserNameById } from '../hooks/useUser';
import { useDeals } from '../hooks/useDeals';
import { getStatusColor, getStatusText, isTradeCompleted } from '../utils/tradeStatusUtils';
import { useUserContext } from '../contexts/UserContext';
import AreYouSure from './AreYouSure';
import StripePaymentForm from './StripePreApproval';
import { useTeam } from '../hooks/useTeam';
import { useTeamRoster } from '../hooks/useTeamRoster';
import apiClient from '../lib/api/apiClient';
import { httpsPlayerImageUrl } from '../utils/stringUtils';
import { formatPositionsList } from '../utils/formatPositionsList';

interface Player {
  id: number;
  rwPlayer: {
    id: number;
    first_name: string;
    last_name: string;
    position_1: string;
    position_2?: string;
    position_3?: string;
    mlb_team?: string;
    image?: string;
    bats?: string;
    throws?: string;
  };
  playerContract?: {
    current_salary: number;
    contractYears?: { year: number; salary: number }[];
  };
  status?: string;
  level?: string;
  is_40_man?: boolean;
  team_id?: number;
  year?: number;
  trade_status?: string | null;
  trade_status_until?: string | null;
}

interface Team {
  id: number;
  name: string;
  abbreviation?: string;
  logo?: string;
  color_primary?: string;
  color_secondary?: string;
  color_text?: string;
  prefix?: string;
}

interface TradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  team1?: number; // team ID
  team2?: number; // team ID
  team1Info?: Team; // team 1 object (if available)
  team2Info?: Team; // team 2 object (if available)
  tradeId?: string; // existing trade ID if this is a continuation
  original?: number; // original trade ID if this is a modification
  initialPlayers1?: Player[]; // initial player data for team 1
  initialPlayers2?: Player[]; // initial player data for team 2
  initialNote?: string; // initial note for the trade
  lastUpdated?: string; // when the trade was last updated
  expires?: string | number | Date; // when the trade expires
  updatedBy?: number | string; // user ID who last updated the trade
  status?: string; // trade status (proposed, approved, declined, etc.)
  onDealWithdrawn?: () => void; // callback when a deal is withdrawn
  team1Accepted?: boolean; // whether team1 has accepted the current trade terms
  team2Accepted?: boolean; // whether team2 has accepted the current trade terms
  viewedAt?: string; // when the trade was last viewed
  initialProtests?: Array<{
    teamId: number;
    note: string;
    created: string;
    createdBy: number;
    username: string;
  }>; // protests array if available
}

interface TradeState {
  team1Players: Player[];
  team2Players: Player[];
  team1Info?: Team;
  team2Info?: Team;
  isLoading: boolean;
  isSubmitting: boolean;
  error?: string;
  note?: string;
  existingNote?: {
    username: string;
    teamName: string;
    message: string;
  };
  dealStatus?: string;
  lastUpdated?: string;
  expires?: string;
  updatedBy?: number;
  team1Accepted?: boolean;
  team2Accepted?: boolean;
  viewedAt?: string;
  original?: number;
  protests?: Array<{
    teamId: number;
    note: string;
    created: string;
    createdBy: number;
    username: string;
  }>;
}

const TradeModal: React.FC<TradeModalProps> = ({
  isOpen,
  onClose,
  team1,
  team2,
  team1Info: passedTeam1Info,
  team2Info: passedTeam2Info,
  tradeId,
  original: passedOriginal,
  initialPlayers1 = [],
  initialPlayers2 = [],
  initialNote = '',
  lastUpdated = '',
  expires = '',
  updatedBy = '',
  status = '',
  team1Accepted = false,
  team2Accepted = false,
  viewedAt = '',
  initialProtests = [],
}) => {
  const [tradeState, setTradeState] = useState<TradeState>({
    team1Players: [],
    team2Players: [],
    isLoading: true,
    isSubmitting: false,
  });

  // Player selection modal state
  const [playerSelectionModal, setPlayerSelectionModal] = useState<{
    isOpen: boolean;
    teamId: number;
    teamInfo?: Team;
  }>({
    isOpen: false,
    teamId: 0,
  });

  // Team selection modal state
  const [isTeamSelectionOpen, setIsTeamSelectionOpen] = useState(false);
  const [tradeCTA, setTradeCTA] = useState<string>('Propose Trade');
  const [tradeCTA2, setTradeCTA2] = useState<string>('Withdraw Trade');

  // Separate change tracking for players vs. notes
  const [hasPlayerChanges, setHasPlayerChanges] = useState(false);
  const [hasNoteChanges, setHasNoteChanges] = useState(false);

  // Payroll change calculations
  const [team1PayrollChange, setTeam1PayrollChange] = useState(0);
  const [team2PayrollChange, setTeam2PayrollChange] = useState(0);

  // Individual team salary totals
  const [team1TotalSalary, setTeam1TotalSalary] = useState(0);
  const [team2TotalSalary, setTeam2TotalSalary] = useState(0);

  // Generic confirmation dialog state
  const [isAreYouSureOpen, setIsAreYouSureOpen] = useState(false);
  const [areYouSureMessage, setAreYouSureMessage] = useState('');
  const [areYouSureFunction, setAreYouSureFunction] = useState<(() => void) | null>(null);

  // Protest state
  const [protestNote, setProtestNote] = useState<string>('');

  // Stripe pre-approval state
  const [isPreApprovalOpen, setIsPreApprovalOpen] = useState(false);
  const [preApprovalAmount, setPreApprovalAmount] = useState(0);
  const [pendingTradeSubmission, setPendingTradeSubmission] = useState<(() => Promise<void>) | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);

  const toast = useModernToast();
  const myLeague = useMyLeagueStore(state => state.myLeague);

  // Get deals functions from useDeals hook
  const { createDeal, updateDeal, withdrawDeal, declineDealForTeam, viewedDeal, agreeToDeal, getDealById, protestDeal } = useDeals(myLeague?.id);

  // Get user name for updatedBy if available
  const { userName: updatedByUserName } = useUserNameById((tradeState.updatedBy || updatedBy) ? Number(tradeState.updatedBy || updatedBy) : undefined);

  // Get current team from UserContext
  const { myTeam } = useUserContext();

  // Get current team's payroll and salary cap for validation
  const { team: currentTeamData } = useTeam(myTeam || undefined);
  const { total_payroll: currentPayroll } = useTeamRoster(myTeam || 1);

  // Determine if current user is the proposer (the team that last updated the trade)
  // isProposer is true if:
  // - No tradeId (new trade being created), OR
  // - myTeam == team1 and team1Accepted == true, OR
  // - myTeam == team2 and team2Accepted == true
  // Use state values when props aren't explicitly provided (e.g., when only tradeId is passed)
  // Recalculate when state changes (e.g., after deal data loads)
  const isProposer = useMemo(() => {
    // If props were explicitly provided (team1/team2 exist), use prop values; otherwise use state
    const currentTeam1 = team1 ?? tradeState.team1Info?.id;
    const currentTeam2 = team2 ?? tradeState.team2Info?.id;
    const currentTeam1Accepted = (team1 !== undefined) ? team1Accepted : (tradeState.team1Accepted ?? false);
    const currentTeam2Accepted = (team2 !== undefined) ? team2Accepted : (tradeState.team2Accepted ?? false);

    return !tradeId || (myTeam && (
      (myTeam === currentTeam1 && currentTeam1Accepted) ||
      (myTeam === currentTeam2 && currentTeam2Accepted)
    ));
  }, [tradeId, myTeam, team1, team2, team1Accepted, team2Accepted, tradeState.team1Info?.id, tradeState.team2Info?.id, tradeState.team1Accepted, tradeState.team2Accepted]);

  // Determine if user can protest this trade
  // Can protest if:
  // - Trade status is "under_review", "league_review", or "completed"
  // - AND myTeam is NOT team1 or team2 (ensuring numeric comparison)
  const canProtest = useMemo(() => {
    if (!tradeId || !myTeam) return false;

    const currentStatus = tradeState.dealStatus || status;
    const protestableStatuses = ['under_review', 'under_league_review'];

    if (!protestableStatuses.includes(currentStatus)) return false;

    // Get current team IDs from state if props are not available, ensure they're numbers
    const currentTeam1Id = Number(team1 ?? tradeState.team1Info?.id);
    const currentTeam2Id = Number(team2 ?? tradeState.team2Info?.id);
    const myTeamId = Number(myTeam);

    // User can protest if they're NOT one of the teams in the trade
    return myTeamId !== currentTeam1Id && myTeamId !== currentTeam2Id;
  }, [tradeId, myTeam, team1, team2, status, tradeState.dealStatus, tradeState.team1Info?.id, tradeState.team2Info?.id]);

  // Check if myTeam has already protested this trade
  const myTeamProtest = useMemo(() => {
    console.log('Checking myTeamProtest:', { myTeam, protests: tradeState.protests });
    if (!myTeam || !tradeState.protests || tradeState.protests.length === 0) {
      console.log('No protests found or no myTeam');
      return null;
    }
    const myTeamId = Number(myTeam);
    const found = tradeState.protests.find(protest => Number(protest.teamId) === myTeamId) || null;
    console.log('Found protest:', found);
    return found;
  }, [myTeam, tradeState.protests]);

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const secondaryText = useColorModeValue('gray.600', 'gray.400');
  const accentColor = useColorModeValue('blue.500', 'blue.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Load initial trade data and mark as viewed if not proposer
  useEffect(() => {
    if (isOpen) {
      loadTradeData();
      setProtestNote(''); // Reset protest note when modal opens

      // If this is an existing trade and user is NOT the proposer, mark it as viewed
      if (tradeId && !isProposer && myTeam && (myTeam === team1 || myTeam === team2)) {
        viewedDeal(parseInt(tradeId), myTeam);
      }
    }
  }, [isOpen, tradeId, isProposer, myTeam, viewedDeal]);

  // Calculate payroll changes whenever players change
  useEffect(() => {
    const team1Salary = tradeState.team1Players.reduce((sum, p) => sum + Number(p.playerContract?.current_salary || 0), 0);
    const team2Salary = tradeState.team2Players.reduce((sum, p) => sum + Number(p.playerContract?.current_salary || 0), 0);

    setTeam1TotalSalary(team1Salary);
    setTeam2TotalSalary(team2Salary);
    setTeam1PayrollChange(team2Salary - team1Salary);
    setTeam2PayrollChange(team1Salary - team2Salary);
  }, [tradeState.team1Players, tradeState.team2Players]);

  // Update CTAs when hasPlayerChanges changes (for non-proposers)
  useEffect(() => {
    if (!isProposer && tradeId) {
      // For non-proposers: show Agree/Decline if no player changes, Counter Offer if changes made
      if (hasPlayerChanges) {
        setTradeCTA('Counter Offer');
        setTradeCTA2('');
      } else {
        setTradeCTA('Agree');
        setTradeCTA2('Decline');
      }
    }
  }, [hasPlayerChanges, isProposer, tradeId]);

  const loadTradeData = async () => {
    setTradeState(prev => ({ ...prev, isLoading: true, error: undefined }));

    try {
      // If we have a tradeId, load existing trade
      let haveTradeData = false;
      if (tradeId && passedTeam1Info && passedTeam2Info && initialPlayers1.length > 0 && initialPlayers2.length > 0) {
        haveTradeData = true;
      }
      if (!haveTradeData && tradeId) {
        // only have the id, so load the deal data using useDeals hook
        const dealData = await getDealById(parseInt(tradeId));
        if (!dealData) {
          throw new Error('Deal not found');
        }

        console.log('Deal data received:', dealData);
        console.log('Players1Details:', dealData.players1Details);
        console.log('Players2Details:', dealData.players2Details);
        console.log('Team1Info:', dealData.team1Info);
        console.log('Team2Info:', dealData.team2Info);

        // Get team info from frontend league data instead of API
        const team1Info = getTeamInfoById(dealData.team1, myLeague);
        const team2Info = getTeamInfoById(dealData.team2, myLeague);

        console.log('Frontend team1Info:', team1Info);
        console.log('Frontend team2Info:', team2Info);

        setTradeState({
          team1Players: dealData.players1Details || [],
          team2Players: dealData.players2Details || [],
          team1Info: team1Info,
          team2Info: team2Info,
          isLoading: false,
          isSubmitting: false,
          dealStatus: dealData.status,
          lastUpdated: dealData.updated,
          expires: dealData.expires,
          updatedBy: dealData.created_by,
          team1Accepted: dealData.team1_accepted,
          team2Accepted: dealData.team2_accepted,
          viewedAt: dealData.viewed_at,
          original: dealData.original,
          protests: dealData.protests || [],
        });

        // For existing trades, start with no changes
        setHasPlayerChanges(false);
        setHasNoteChanges(false);

      } else {
        // Use passed team objects if available, otherwise find them in myLeague data
        let team1Info: Team | undefined = passedTeam1Info;
        let team2Info: Team | undefined = passedTeam2Info;

        // If we don't have team objects, try to find them in myLeague data
        if (!team1Info) {
          team1Info = getTeamInfoById(team1, myLeague);
        }
        if (!team2Info) {
          team2Info = getTeamInfoById(team2, myLeague);
        }

        // Use initial player data if provided, otherwise show empty teams
        const team1Players: Player[] = initialPlayers1 || [];
        const team2Players: Player[] = initialPlayers2 || [];

        setTradeState({
          team1Players,
          team2Players,
          team1Info,
          team2Info,
          isLoading: false,
          isSubmitting: false,
          note: tradeId ? '' : initialNote, // Empty for existing trades, initialNote for new trades
          original: passedOriginal,
          protests: initialProtests || [], // Use initialProtests if provided
        });

        // For new trades, start with no changes
        setHasPlayerChanges(false);
        setHasNoteChanges(false);

      }

      // Set trade CTA based on isProposer and whether changes were made
      if (isProposer) {
        setTradeCTA(!tradeId ? 'Propose Trade' : 'Update Trade');
        setTradeCTA2(!tradeId ? '' : 'Withdraw Trade');
      } else {
        // For non-proposers: always start with Agree/Decline
        // The useEffect will update these when hasChanges changes
        setTradeCTA('Agree');
        setTradeCTA2('Decline');
      }
    } catch (error) {
      console.error('Error loading trade data:', error);
      // Even if API calls fail, we can still show a basic trade modal
      setTradeState(prev => ({
        ...prev,
        isLoading: false,
        error: undefined, // Don't show error, just show empty state
      }));
    }
  };

  // Check if trade would exceed salary cap and calculate excess amount
  const checkSalaryCapExceedance = async (): Promise<{ exceeds: boolean; excessAmount: number }> => {
    if (!myTeam || !currentTeamData?.salary_cap || !currentPayroll || !myLeague?.id) {
      return { exceeds: false, excessAmount: 0 };
    }

    // Determine which team is the current user's team
    const currentTeam1Id = team1 || tradeState.team1Info?.id;
    const currentTeam2Id = team2 || tradeState.team2Info?.id;
    const isMyTeam1 = myTeam === currentTeam1Id;
    const isMyTeam2 = myTeam === currentTeam2Id;

    if (!isMyTeam1 && !isMyTeam2) {
      // User is not part of this trade, no need to check
      return { exceeds: false, excessAmount: 0 };
    }

    // Calculate this trade's payroll change for the user's team
    const thisTradePayrollChange = isMyTeam1 ? team1PayrollChange : team2PayrollChange;
    console.log('thisTradePayrollChange:', thisTradePayrollChange);

    // If the trade lowers or maintains the payroll, it won't cause a salary cap exceedance
    if (thisTradePayrollChange <= 0) {
      console.log('Trade lowers or maintains payroll, skipping exceedance check');
      return { exceeds: false, excessAmount: 0 };
    }

    // Get the max salary change from all outstanding deals for this team
    const response = await apiClient.get<{ positiveChange: boolean; positiveChangeAmount: number }>(
      `/deals/getMaxSalaryChangeFromDealsForTeam/${myTeam}/${myLeague.id}`
    );
    const { positiveChange, positiveChangeAmount } = response.data;
    console.log('positiveChange:', positiveChange);
    console.log('maxSalaryChange:', positiveChangeAmount);

    // Calculate the worst-case payroll scenario:
    // - If outstanding deals have a positive max change (increasing payroll), add it
    // - For new trades (no tradeId), add this trade's change on top (it's not in the database yet)
    // - For existing trades (has tradeId), don't add it again (it's already included in the backend calculation)
    const outstandingDealsImpact = positiveChange ? positiveChangeAmount : 0;
    console.log('outstandingDealsImpact:', outstandingDealsImpact);
    // Only add this trade's payroll change for new trades (not yet in database)
    const tradeImpact = tradeId ? 0 : thisTradePayrollChange;
    console.log('tradeImpact:', tradeImpact, tradeId ? '(existing trade, already counted)' : '(new trade, adding to calculation)');
    const newPayroll = currentPayroll + outstandingDealsImpact + tradeImpact;
    console.log('newPayroll:', newPayroll);
    // Salary cap is in millions, convert to actual dollars
    const salaryCapInDollars = Number(currentTeamData.salary_cap) * 1000000;
    console.log('salaryCapInDollars:', salaryCapInDollars);
    // Check if new payroll exceeds salary cap
    if (newPayroll > salaryCapInDollars) {
      console.log('newPayroll exceeds salaryCapInDollars');
      const excessAmount = newPayroll - salaryCapInDollars;
      console.log('excessAmount:', excessAmount);
      return { exceeds: true, excessAmount };
    }

    return { exceeds: false, excessAmount: 0 };
  };

  // Actual trade submission logic (extracted for reuse)
  // Accept optional paymentIntentId parameter to avoid race condition with state updates
  const submitTradeInternal = async (overridePaymentIntentId?: string | null) => {
    console.log('submitTradeInternal called with:', {
      overridePaymentIntentId,
      statePaymentIntentId: paymentIntentId,
      tradeId
    });

    // Use state-based team IDs as fallback if props are not available
    const currentTeam1Id = team1 || tradeState.team1Info?.id;
    const currentTeam2Id = team2 || tradeState.team2Info?.id;

    if (!currentTeam1Id || !currentTeam2Id) {
      toast.error('Error', 'Both teams are required for a trade');
      return;
    }

    if (!myLeague?.id) {
      toast.error('Error', 'League information not available');
      return;
    }

    // Use overridePaymentIntentId if provided, otherwise fall back to state
    const effectivePaymentIntentId = overridePaymentIntentId !== undefined ? overridePaymentIntentId : paymentIntentId;
    console.log('Effective paymentIntentId:', effectivePaymentIntentId);

    setTradeState(prev => ({ ...prev, isSubmitting: true }));

    try {
      // Check if this is an "Agree" action for non-proposers
      if (tradeCTA === 'Agree' && !isProposer && tradeId) {
        // Non-proposer is agreeing to the trade as-is
        const result = await agreeToDeal(parseInt(tradeId), tradeState.note);
        if (result) {
          toast.success('Success', 'Trade accepted successfully!');
          onClose();
        } else {
          toast.error('Error', 'Failed to accept trade');
        }
        return;
      }

      if (!tradeId) {
        // Create new trade using useDeals hook
        const dealData: any = {
          league_id: myLeague.id,
          type: 'trade' as const,
          status: 'proposed' as const,
          team1: currentTeam1Id,
          players1: tradeState.team1Players.map(p => p.id),
          team2: currentTeam2Id,
          players2: tradeState.team2Players.map(p => p.id),
          note: tradeState.note,
          offer: { team1PayrollChange, team2PayrollChange }
        };

        // Add paymentIntentId to deal metadata if available (for linking)
        if (effectivePaymentIntentId) {
          dealData.paymentIntentId = effectivePaymentIntentId;
        }

        console.log('Creating deal with data:', { dealData, effectivePaymentIntentId });
        const newDeal = await createDeal(dealData);
        console.log('createDeal returned:', newDeal);

        if (newDeal) {
          console.log('Deal created successfully, checking metadata update conditions:', {
            hasPaymentIntentId: !!effectivePaymentIntentId,
            paymentIntentId: effectivePaymentIntentId,
            hasDealId: !!newDeal.id,
            dealId: newDeal.id,
            newDealKeys: Object.keys(newDeal)
          });

          // For new trades, update PaymentIntent metadata with dealId after creation
          // This creates a two-way link: deal has paymentIntentId, PaymentIntent has dealId
          // Note: Backend must support POST /store/update-payment-intent-metadata endpoint
          // Alternative: Backend could handle this automatically when creating deals with paymentIntentId
          if (effectivePaymentIntentId && newDeal.id) {
            try {
              console.log('Updating PaymentIntent metadata for new deal:', { paymentIntentId: effectivePaymentIntentId, dealId: newDeal.id });
              await apiClient.post('/store/update-payment-intent-metadata', {
                paymentIntentId: effectivePaymentIntentId,
                metadata: { dealId: String(newDeal.id) }
              });
              console.log('Successfully linked PaymentIntent to new deal:', { paymentIntentId: effectivePaymentIntentId, dealId: newDeal.id });
            } catch (error) {
              console.error('Failed to update PaymentIntent metadata with dealId:', error);
              // Don't fail the trade creation if metadata update fails - the deal still has paymentIntentId
            }
          } else {
            console.warn('Skipping PaymentIntent metadata update:', {
              hasPaymentIntentId: !!effectivePaymentIntentId,
              hasDealId: !!newDeal.id,
              paymentIntentId: effectivePaymentIntentId,
              dealId: newDeal.id,
              newDealFull: newDeal
            });
          }

          toast.success('Success', 'Trade proposal submitted successfully!');
          setPaymentIntentId(null); // Clear after successful linking
          onClose();
        } else {
          console.error('createDeal returned null or falsy value');
          toast.error('Error', 'Failed to create trade proposal');
        }
      } else {
        // Update existing trade using useDeals hook
        const dealData: any = {
          team1: currentTeam1Id,
          players1: tradeState.team1Players.map(p => p.id),
          team2: currentTeam2Id,
          players2: tradeState.team2Players.map(p => p.id),
          note: tradeState.note,
          offer: { team1PayrollChange, team2PayrollChange }
        };

        // Add paymentIntentId to deal metadata if available (for linking)
        if (effectivePaymentIntentId) {
          dealData.paymentIntentId = effectivePaymentIntentId;
        }

        const updatedDeal = await updateDeal(parseInt(tradeId), dealData, myTeam);
        if (updatedDeal) {
          // For existing trades, PaymentIntent metadata should already have dealId (from payload.tradeId/dealId)
          // But we ensure it's set in case it wasn't already, creating a two-way link
          // Note: Backend must support POST /store/update-payment-intent-metadata endpoint
          // Alternative: Backend could handle this automatically when updating deals with paymentIntentId
          if (effectivePaymentIntentId && updatedDeal.id) {
            try {
              console.log('Updating PaymentIntent metadata for existing deal:', { paymentIntentId: effectivePaymentIntentId, dealId: updatedDeal.id });
              await apiClient.post('/store/update-payment-intent-metadata', {
                paymentIntentId: effectivePaymentIntentId,
                metadata: { dealId: String(updatedDeal.id) }
              });
              console.log('Successfully linked PaymentIntent to existing deal:', { paymentIntentId: effectivePaymentIntentId, dealId: updatedDeal.id });
            } catch (error) {
              console.error('Failed to update PaymentIntent metadata with dealId:', error);
              // Don't fail the trade update if metadata update fails - the deal still has paymentIntentId
            }
          } else {
            console.warn('Skipping PaymentIntent metadata update:', {
              hasPaymentIntentId: !!effectivePaymentIntentId,
              hasDealId: !!updatedDeal.id,
              paymentIntentId: effectivePaymentIntentId,
              dealId: updatedDeal.id
            });
          }

          toast.success('Success', 'Trade updated successfully!');
          setPaymentIntentId(null); // Clear after successful linking
          onClose();
        } else {
          toast.error('Error', 'Failed to update trade');
        }
      }
    } catch (error) {
      console.error('Error submitting trade:', error);
      toast.error('Error', 'Failed to submit trade proposal');
    } finally {
      setTradeState(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleSubmitTrade = async () => {
    // Check if trade would exceed salary cap
    const { exceeds, excessAmount } = await checkSalaryCapExceedance();

    if (exceeds) {
      // Store the submission function and show pre-approval modal
      setPendingTradeSubmission(() => submitTradeInternal);
      setPreApprovalAmount(excessAmount);
      setIsPreApprovalOpen(true);
      return;
    }

    // No salary cap issue, proceed with submission
    await submitTradeInternal();
  };

  // Handle pre-approval success
  const handlePreApprovalSuccess = async (result: any) => {
    const { paymentIntent, paymentIntentId: piId } = result;
    console.log('Pre-approval successful, submitting trade...', { paymentIntent, paymentIntentId: piId });

    // Store paymentIntentId for linking with deal
    if (piId) {
      setPaymentIntentId(piId);

      // Update purchase status to pending
      try {
        await apiClient.put(`/purchases/${piId}/status/pending`);
        console.log('Purchase status updated to pending:', piId);
      } catch (error) {
        console.error('Error updating purchase status to pending:', error);
        // Don't fail the entire flow if purchase update fails
      }
    }

    // Close the pre-approval modal
    setIsPreApprovalOpen(false);

    // Proceed with trade submission - pass paymentIntentId directly to avoid race condition
    if (pendingTradeSubmission) {
      try {
        console.log('Calling pending trade submission with paymentIntentId:', piId);
        // Pass paymentIntentId directly to avoid race condition with state update
        await submitTradeInternal(piId);
        console.log('Trade submitted successfully after pre-approval');
        setPendingTradeSubmission(null);
      } catch (error) {
        console.error('Error submitting trade after pre-approval:', error);
        // Error is already handled in submitTradeInternal, but we should clear the pending submission
        setPendingTradeSubmission(null);
      }
    } else {
      console.warn('No pending trade submission found after pre-approval success');
      toast.error('Error', 'Trade submission failed - no pending submission found');
    }
  };

  // Handle pre-approval failure/close
  const handlePreApprovalClose = async () => {
    // If paymentIntentId exists, a purchase was created and should be deleted
    if (paymentIntentId) {
      try {
        await apiClient.delete(`/purchases/${paymentIntentId}`);
        console.log('Purchase deleted:', paymentIntentId);
      } catch (error) {
        console.error('Error deleting purchase:', error);
        // Don't show error to user, just log it
      }
    }

    setIsPreApprovalOpen(false);
    setPendingTradeSubmission(null);
    setPaymentIntentId(null); // Clear paymentIntentId on close
    toast.error('Error', 'Payment authorization is required to proceed with this trade. Please adjust your trade to stay within your salary cap or authorize the payment.');
  };

  const executeDeclineTrade = async () => {
    try {
      if (tradeId && team1 && team2 && myTeam) {
        // Call the dealsHook to decline the trade
        const result = await declineDealForTeam(parseInt(tradeId), myTeam, tradeState.note);

        if (result) {
          toast.success('Success', 'Trade declined successfully');
          onClose();
        } else {
          toast.error('Error', 'Decline failed - no result returned');
        }
      } else {
        toast.error('Error', 'No trade ID or team ID found');
      }
    } catch (error) {
      console.error('Error declining trade:', error);
      toast.error('Error', 'Failed to decline trade');
    }
  };

  const executeWithdrawTrade = async () => {
    try {
      if (tradeId && team1) {
        // Call the dealsHook to withdraw the trade
        const result = await withdrawDeal(parseInt(tradeId), team1, tradeState.note);

        if (result) {
          toast.success('Success', 'Trade withdrawn successfully');
          onClose();
        } else {
          toast.error('Error', 'Withdrawal failed - no result returned');
        }
      } else {
        toast.error('Error', 'No trade ID or team ID found');
      }
    } catch (error) {
      console.error('Error withdrawing trade:', error);
      toast.error('Error', 'Failed to withdraw trade');
    }
  };

  const handleProtestTrade = async () => {
    if (!protestNote || protestNote.trim().length < 20) {
      toast.error('Error', 'A note of at least 20 characters is required to protest a trade');
      return;
    }

    if (protestNote.trim().length > 500) {
      toast.error('Error', 'Note must be 500 characters or less');
      return;
    }

    if (!tradeId || !myTeam) {
      toast.error('Error', 'Missing trade or team information');
      return;
    }

    setTradeState(prev => ({ ...prev, isSubmitting: true }));

    try {
      const result = await protestDeal(parseInt(tradeId), myTeam, protestNote);

      if (result) {
        toast.success('Success', 'Trade protest submitted successfully');
        setProtestNote('');
        onClose();
      } else {
        toast.error('Error', 'Failed to submit protest');
      }
    } catch (error) {
      console.error('Error protesting trade:', error);

      // Extract the actual error message from the backend
      let errorMessage = 'Failed to submit trade protest';

      // Handle different error structures
      if (error && typeof error === 'object') {
        // Check if error has a message property directly
        if ('message' in error) {
          errorMessage = String(error.message);
        }
        // Check if error has a nested error object with message
        else if ('error' in error && error.error && typeof error.error === 'object' && error.error !== null && 'message' in (error.error as Record<string, unknown>)) {
          errorMessage = String((error.error as Record<string, unknown>).message);
        }
        // Check if error has a response object with data
        else if ('response' in error && error.response && typeof error.response === 'object' && error.response !== null && 'data' in error.response && error.response.data) {
          const responseData = error.response.data;
          if (typeof responseData === 'string') {
            errorMessage = responseData;
          } else if (typeof responseData === 'object' && responseData !== null && 'message' in responseData) {
            errorMessage = String(responseData.message);
          }
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      // Display the actual backend error message
      toast.error('Error', errorMessage);
    } finally {
      setTradeState(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleTradeCTA2 = async () => {
    setTradeState(prev => ({ ...prev, isSubmitting: true }));

    try {
      switch (tradeCTA2) {
        case 'Cancel':
          onClose();
          return;
        case 'Decline':
          // Show confirmation dialog for decline
          setAreYouSureMessage('Are you sure you want to decline this trade?');
          setAreYouSureFunction(() => executeDeclineTrade);
          setIsAreYouSureOpen(true);
          return;
        case 'Withdraw Trade':
          // Show confirmation dialog for withdraw
          setAreYouSureMessage('Are you sure you want to withdraw from this trade?');
          setAreYouSureFunction(() => executeWithdrawTrade);
          setIsAreYouSureOpen(true);
          return;
        case 'Counter Offer':
          // For counter offer, we just close the modal and let the user modify the trade
          onClose();
          return;
        default:
          console.warn('Unknown tradeCTA2 action:', tradeCTA2);
          return;
      }
    } catch (error) {
      console.error('TradeModal: Caught error in handleTradeCTA2:', error);

      // Extract the actual error message from the backend
      let errorMessage = 'Failed to process trade action';

      // Handle different error structures
      if (error && typeof error === 'object') {
        // Check if error has a message property directly
        if ('message' in error) {
          errorMessage = String(error.message);
        }
        // Check if error has a nested error object with message
        else if ('error' in error && error.error && typeof error.error === 'object' && error.error !== null && 'message' in (error.error as Record<string, unknown>)) {
          errorMessage = String((error.error as Record<string, unknown>).message);
        }
        // Check if error has a response object with data
        else if ('response' in error && error.response && typeof error.response === 'object' && error.response !== null && 'data' in error.response && error.response.data) {
          const responseData = error.response.data;
          if (typeof responseData === 'string') {
            errorMessage = responseData;
          } else if (typeof responseData === 'object' && responseData !== null && 'message' in responseData) {
            errorMessage = String(responseData.message);
          }
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      // Check if the error is about child deal creation (which means withdrawal actually succeeded)
      if (errorMessage.includes('child deal') || errorMessage.includes('Validation error')) {
        toast.success('Success', 'Trade withdrawn successfully (with minor tracking issue)');
        onClose();
      } else {
        // Display the actual backend error message
        toast.error('Error', errorMessage);
      }
    } finally {
      // Always reset loading state
      setTradeState(prev => ({ ...prev, isSubmitting: false }));
    }
  };



  const handlePlayerSelect = (players: Player[]) => {
    // Determine which team is being edited by comparing with team1 ID
    // Use state-based team ID as fallback if prop is not available
    const team1Id = team1 || tradeState.team1Info?.id;
    const isTeam1 = playerSelectionModal.teamId === team1Id;

    setTradeState(prev => ({
      ...prev,
      team1Players: isTeam1 ? players : prev.team1Players,
      team2Players: !isTeam1 ? players : prev.team2Players,
    }));

    // Mark that player changes have been made
    setHasPlayerChanges(true);

    // Close the modal
    setPlayerSelectionModal({ isOpen: false, teamId: 0 });
  };

  const handleTeamSelect = (teamId: string) => {
    // Find the team info from myLeague data
    const selectedTeamInfo = getTeamInfoById(parseInt(teamId), myLeague);

    if (!selectedTeamInfo) {
      console.error('Team info not found for team ID:', teamId);
      setIsTeamSelectionOpen(false);
      return;
    }

    // Update team2 info in state
    setTradeState(prev => ({ ...prev, team2Info: selectedTeamInfo }));
    setIsTeamSelectionOpen(false);

    // Automatically open player selection modal for the selected team
    setTimeout(() => {
      setPlayerSelectionModal({
        isOpen: true,
        teamId: selectedTeamInfo.id,
        teamInfo: selectedTeamInfo,
      });
    }, 100); // Small delay to ensure team selection modal closes first
  };

  const handleRemovePlayer = (playerToRemove: Player) => {
    setTradeState(prev => ({
      ...prev,
      team1Players: prev.team1Players.filter(p => p.id !== playerToRemove.id),
      team2Players: prev.team2Players.filter(p => p.id !== playerToRemove.id),
    }));

    // Mark that player changes have been made
    setHasPlayerChanges(true);
  };

  const renderPlayerCard = (player: Player, teamInfo?: Team) => {
    const rwPlayer = player.rwPlayer;
    const positions = formatPositionsList(player);

    const salary = player.playerContract?.current_salary || 0;
    const formattedSalary = salary ? `$${(salary / 1000000).toFixed(2)}M` : 'Min';

    return (
      <Box
        key={player.id}
        p={3}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="md"
        bg={bgColor}
        _hover={{ bg: hoverBg }}
      >
        <HStack spacing={3}>
          {httpsPlayerImageUrl(rwPlayer.image) ? (
            <Box
              w="32px"
              h="32px"
              borderRadius="full"
              overflow="hidden"
              bg="transparent"
            >
              <img
                src={httpsPlayerImageUrl(rwPlayer.image)}
                alt={`${rwPlayer.first_name} ${rwPlayer.last_name}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </Box>
          ) : (
            <Avatar
              size="sm"
              name={`${rwPlayer.first_name} ${rwPlayer.last_name}`}
              bg={teamInfo?.color_primary || 'gray.300'}
              color={teamInfo?.color_text || 'white'}
              border={`2px solid ${teamInfo?.color_secondary || 'transparent'}`}
            />
          )}
          <Box flex="1">
            <Text fontWeight="semibold" color={textColor}>
              {rwPlayer.first_name} {rwPlayer.last_name}
            </Text>
            <HStack spacing={2} align="center">
              <Text fontSize="sm" color={secondaryText}>
                {positions}  •   {formattedSalary} • {player.level || 'Unknown'}
              </Text>
              {player.is_40_man && (
                <Badge colorScheme="blue" size="sm">40-MAN</Badge>
              )}
            </HStack>
          </Box>
          {(() => {
            // Check if trade should be marked as expired
            const isExpired = (tradeState.dealStatus || status) !== 'under_review' &&
              (tradeState.dealStatus || status) !== 'league_review' &&
              (tradeState.expires || expires) &&
              new Date(tradeState.expires || expires) < new Date();

            // Hide delete icon for review statuses, settlement statuses, and expired trades
            return !(tradeState.dealStatus || status)?.toLowerCase().includes('review') &&
              !(tradeState.dealStatus || status)?.toLowerCase().includes('settlement') &&
              !isExpired;
          })() && (
              <Icon
                as={FaTrash}
                color="gray.300"
                boxSize={4}
                cursor="pointer"
                _hover={{ color: "white" }}
                onClick={() => handleRemovePlayer(player)}
              />
            )}
        </HStack>
      </Box>
    );
  };

  const renderTeamSection = (
    title: string,
    players: Player[],
    teamInfo?: Team,
    isTeam1: boolean = false
  ) => {


    return (
      <Box flex="1" display="flex" flexDirection="column" h="100%">
        <HStack mb={4} align="center" justify="space-between">
          <HStack>
            {teamInfo && (
              <TeamLogo
                team={{
                  name: getTeamName(teamInfo),
                  abbreviation: teamInfo.abbreviation,
                  logo: getTeamLogo(teamInfo),
                  color_primary: teamInfo.color_primary,
                  color_secondary: teamInfo.color_secondary,
                  color_text: teamInfo.color_text
                }}
                size="md"
              />
            )}
            <Box>
              <Text fontWeight="bold" color={textColor} fontSize="lg">
                {getTeamName(teamInfo || null)}
              </Text>
            </Box>
          </HStack>

          {/* Edit Icon - only show when there are players and deal is not under review/settlement */}
          {(() => {
            // Check if trade should be marked as expired
            const isExpired = (tradeState.dealStatus || status) !== 'under_review' &&
              (tradeState.dealStatus || status) !== 'league_review' &&
              (tradeState.expires || expires) &&
              new Date(tradeState.expires || expires) < new Date();

            // Hide edit icon for review statuses, settlement statuses, and expired trades
            return players.length > 0 &&
              !(tradeState.dealStatus || status)?.toLowerCase().includes('review') &&
              !(tradeState.dealStatus || status)?.toLowerCase().includes('settlement') &&
              !isExpired;
          })() && (
              <Icon
                as={FaEdit}
                color={accentColor}
                boxSize={6}
                cursor="pointer"
                _hover={{ color: 'blue.600' }}
                onClick={() => {
                  if (teamInfo) {
                    setPlayerSelectionModal({
                      isOpen: true,
                      teamId: teamInfo.id,
                      teamInfo,
                    });
                  }
                }}
              />
            )}
          {/* Change Team button - for new trades, right side, no players selected */}
          {!isTeam1 && teamInfo && !tradeId && players.length === 0 && (
            <Button
              size="xs"
              variant="ghost"
              color={accentColor}
              _hover={{ bg: 'whiteAlpha.200' }}
              onClick={() => setIsTeamSelectionOpen(true)}
            >
              Change Team
            </Button>
          )}
        </HStack>

        <VStack spacing={2} align="stretch" flex="1">
          {players.length > 0 ? (
            players.map(player => renderPlayerCard(player, teamInfo))
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              minH="200px"
            >
              <Button
                variant="dominus"
                size="md"
                onClick={() => {
                  if (teamInfo) {
                    setPlayerSelectionModal({
                      isOpen: true,
                      teamId: teamInfo.id,
                      teamInfo,
                    });
                  } else {
                    setIsTeamSelectionOpen(true);
                  }
                }}
              >
                {teamInfo ? 'Select a Player' : 'Select Team'}
              </Button>
            </Box>
          )}

          {/* Salary Cap Change */}
          {players.length > 0 && (
            <Box
              p={3}
              bg={bgColor}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              mt="auto"
            >
              <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                Salary Cap Change
              </Text>
              <HStack spacing={2} align="baseline">
                <Text fontSize="sm" fontWeight="bold" color={(() => {
                  const netChange = isTeam1 ? team1PayrollChange : team2PayrollChange;
                  return netChange < 0 ? 'red.400' : netChange > 0 ? 'green.400' : 'white';
                })()}>
                  {(() => {
                    const netChange = isTeam1 ? team1PayrollChange : team2PayrollChange;
                    return netChange !== 0
                      ? `${netChange > 0 ? '+' : '-'}$${(Math.abs(netChange) / 1000000).toFixed(2)}M`
                      : '$0.00M';
                  })()}
                </Text>
                <Text fontSize="sm" color={secondaryText}>
                  {(() => {
                    const incomingSalary = isTeam1 ? team2TotalSalary : team1TotalSalary;
                    const outgoingSalary = isTeam1 ? team1TotalSalary : team2TotalSalary;

                    return `(${incomingSalary > 0 ? `+$${(incomingSalary / 1000000).toFixed(2)}M` : '$0.00M'} incoming - ${outgoingSalary > 0 ? `$${(outgoingSalary / 1000000).toFixed(2)}M` : '$0.00M'} outgoing)`;
                  })()}
                </Text>
              </HStack>
            </Box>
          )}
        </VStack>
      </Box>
    );
  };

  if (tradeState.isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalBody p={8} textAlign="center">
            <Spinner size="xl" color={accentColor} />
            <Text mt={4} color={textColor}>Loading trade data...</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="6xl">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent
          bg={bgColor}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="xl"
          boxShadow="2xl"
          maxW="1200px"
        >
          <ModalHeader
            borderBottom="1px solid"
            borderColor={borderColor}
            pb={4}
            display="flex"
            alignItems="center"
            gap={3}
          >
            <Icon as={FaExchangeAlt} color={accentColor} boxSize={5} />
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              {tradeId ? (
                <HStack spacing={3} align="center">
                  <Text>Trade</Text>
                  <Badge
                    colorScheme={getStatusColor(tradeState.dealStatus || status)}
                    borderRadius="md"
                    px={2}
                    py={1}
                    fontSize="lg"
                    fontWeight="bold"
                  >
                    {(() => {
                      // Check if trade should be marked as expired
                      const st = (tradeState.dealStatus || status || '').toLowerCase();
                      // Don't mark as expired if status is already a terminal status
                      const isTerminalStatus = ['rejected', 'canceled', 'completed', 'complete', 'declined', 'withdrawn', 'approved'].includes(st);
                      const isExpired = !isTerminalStatus &&
                        st !== 'under_review' &&
                        st !== 'league_review' &&
                        (tradeState.expires || expires) &&
                        new Date(tradeState.expires || expires) < new Date();

                      if (isExpired) {
                        return 'EXPIRED';
                      }

                      return getStatusText(tradeState.dealStatus || status);
                    })()}
                  </Badge>
                  <span style={{ fontWeight: 'normal', fontSize: '14px', color: secondaryText }}>
                    {(() => {
                      const currentStatus = tradeState.dealStatus || status;
                      if (currentStatus && currentStatus.includes('settlement')) {
                        return ' - Settles at midnight';
                      }
                      if (currentStatus && (currentStatus.toLowerCase() === 'completed' || currentStatus.toLowerCase() === 'complete')) {
                        return ` - Settled ${new Date(tradeState.lastUpdated || lastUpdated).toLocaleDateString()}`;
                      }
                      if (currentStatus === 'rejected') {
                        return ` - Canceled by the commissioner on ${new Date(tradeState.lastUpdated || lastUpdated).toLocaleDateString()}`;
                      }
                      if (currentStatus === 'canceled') {
                        return ` - Canceled by the Dominus Commissioner on ${new Date(tradeState.lastUpdated || lastUpdated).toLocaleDateString()}`;
                      }
                      return ` - ${isProposer ? 'You' : (updatedByUserName || `User ${tradeState.updatedBy}` || 'Unknown')} updated at ${displayTimeStamp(tradeState.lastUpdated || lastUpdated)}`;
                    })()}
                    {tradeId && tradeState.viewedAt && isProposer && ` • Viewed at ${displayTimeStamp(tradeState.viewedAt)}`}
                  </span>
                </HStack>
              ) : 'Proposed Trade'}
            </Text>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody p={6}>
            {tradeState.error && (
              <Alert status="error" mb={4} borderRadius="md">
                <AlertIcon />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{tradeState.error}</AlertDescription>
              </Alert>
            )}

            <HStack spacing={8} align="stretch" h="100%">
              {/* Team 1 Section */}
              <Box flex="1" display="flex" flexDirection="column">
                {renderTeamSection(
                  '',
                  tradeState.team1Players,
                  tradeState.team1Info,
                  true
                )}
              </Box>

              {/* Exchange Icon */}
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minH="200px"
              >
                <Icon
                  as={FaExchangeAlt}
                  color={accentColor}
                  boxSize={8}
                  mb={2}
                />
                <Text fontSize="sm" color={secondaryText} textAlign="center">
                  {(() => {
                    const s = (tradeState.dealStatus || status || '').toLowerCase();
                    const isProposedTrade = ['accepted', 'settlement_trade', 'under_review', 'league_review', 'under_league_review', 'completed'].includes(s);
                    return (
                      <>
                        {!isProposedTrade && (
                          <>
                            Proposed<br />
                          </>
                        )}
                        Trade
                      </>
                    );
                  })()}
                </Text>
              </Box>

              {/* Team 2 Section */}
              <Box flex="1" display="flex" flexDirection="column">
                {renderTeamSection(
                  '',
                  tradeState.team2Players,
                  tradeState.team2Info,
                  false
                )}
              </Box>
            </HStack>

            {/* Notes Section - Full Width */}
            <Box
              p={4}
              bg={bgColor}
              borderTop="1px solid"
              borderColor={borderColor}
              mt={4}
            >
              {/* Previous Note from User */}
              {(() => {
                const currentStatus = tradeState.dealStatus || status;
                const isRejectedOrCanceled = currentStatus === 'rejected' || currentStatus === 'canceled';
                // Show note section if: (1) we have a note, OR (2) it's a rejected/canceled deal (to show commissioner note)
                return tradeId && (initialNote || isRejectedOrCanceled);
              })() && (
                  <Box mb={4} p={3} bg="gray.700" borderRadius="md" border="1px solid" borderColor="gray.600">
                    <HStack mb={2} alignItems="flex-start">
                      <Icon as={FaComments} color="gray.400" boxSize={3} />
                      <Text fontSize="xs" color="gray.300" fontWeight="semibold">
                        {(() => {
                          const currentStatus = tradeState.dealStatus || status;
                          if (currentStatus === 'rejected') {
                            return 'Commissioner wrote:';
                          }
                          if (currentStatus === 'canceled') {
                            return 'Commissioner wrote:';
                          }
                          return isProposer ? 'Your previous note:' : `${updatedByUserName || `User ${updatedBy}`} wrote:`;
                        })()}
                      </Text>
                    </HStack>
                    {initialNote ? (
                      <Text fontSize="sm" color="gray.200" mt={2}>
                        {initialNote}
                      </Text>
                    ) : (
                      <Text fontSize="sm" color="gray.400" fontStyle="italic" mt={2}>
                        No note provided.
                      </Text>
                    )}
                  </Box>
                )}

              {/* Include a Note Section - Only show if trade is not completed and not in special statuses */}
              {(() => {
                // Check if trade should be marked as expired
                const isExpired = (tradeState.dealStatus || status) !== 'under_review' &&
                  (tradeState.dealStatus || status) !== 'league_review' &&
                  (tradeState.expires || expires) &&
                  new Date(tradeState.expires || expires) < new Date();

                // Hide note section for completed trades, special statuses, review statuses, settlement statuses, expired trades, and canceled trades
                return !isTradeCompleted(tradeState.dealStatus || status) &&
                  !['has_salary_issue', 'completed', 'canceled'].includes(tradeState.dealStatus || status) &&
                  !(tradeState.dealStatus || status)?.toLowerCase().includes('review') &&
                  !(tradeState.dealStatus || status)?.toLowerCase().includes('settlement') &&
                  !isExpired;
              })() && (
                  <>
                    <HStack mb={3}>
                      <Icon as={FaComments} color={accentColor} boxSize={4} />
                      <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                        Include a Note
                      </Text>
                    </HStack>

                    {/* Existing Note from Proposer */}
                    {tradeId && tradeState.existingNote && (
                      <Box mb={3} p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                        <Text fontSize="xs" color="blue.600" fontWeight="semibold" mb={1}>
                          {tradeState.existingNote.username} from {tradeState.existingNote.teamName}
                        </Text>
                        <Text fontSize="sm" color="gray.700">
                          {tradeState.existingNote.message}
                        </Text>
                      </Box>
                    )}

                    {/* Note Input */}
                    <Textarea
                      placeholder={tradeId ? "Add your response..." : "Add a note about this trade proposal..."}
                      value={tradeState.note}
                      onChange={(e) => {
                        setTradeState(prev => ({ ...prev, note: e.target.value }));
                        setHasNoteChanges(true);
                      }}
                      maxLength={500}
                      size="sm"
                      resize="none"
                      rows={3}
                      bg={bgColor}
                      borderColor={borderColor}
                      _focus={{ borderColor: accentColor }}
                    />

                    {/* Character Count */}
                    <Text fontSize="xs" color={secondaryText} textAlign="right" mt={1}>
                      {tradeState.note?.length || 0}/500
                    </Text>
                  </>
                )}

              {/* Protest Section - Only show if user can protest */}
              {canProtest && (
                <>
                  <Box
                    borderTop="1px solid"
                    borderColor={borderColor}
                    pt={4}
                    mt={4}
                  >
                    <HStack mb={3}>
                      <Icon as={FaComments} color="red.500" boxSize={4} />
                      <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                        Protest This Trade
                      </Text>
                    </HStack>

                    {myTeamProtest ? (
                      // Show message if team has already protested
                      <Box
                        bg={useColorModeValue('orange.50', 'orange.900')}
                        border="1px solid"
                        borderColor={useColorModeValue('orange.300', 'orange.400')}
                        borderRadius="md"
                        p={4}
                        mb={3}
                      >
                        <Text fontSize="sm" color={useColorModeValue('orange.800', 'orange.200')} fontWeight="semibold" mb={1}>
                          Your team has already protested this trade
                        </Text>
                        <Text fontSize="xs" color={useColorModeValue('orange.700', 'orange.300')}>
                          on {new Date(myTeamProtest.created).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </Text>
                      </Box>
                    ) : (
                      // Show protest form if team hasn't protested yet
                      <>
                        <Text fontSize="xs" color={secondaryText} mb={3}>
                          If you believe this trade violates league rules or is unfair, you can submit a protest. A note explaining your concern is required (20-500 characters).
                        </Text>

                        {/* Protest Note Input */}
                        <Textarea
                          placeholder="Explain why you are protesting this trade..."
                          value={protestNote}
                          onChange={(e) => setProtestNote(e.target.value)}
                          minLength={20}
                          maxLength={500}
                          size="sm"
                          resize="none"
                          rows={3}
                          bg={bgColor}
                          borderColor={borderColor}
                          _focus={{ borderColor: accentColor }}
                          isRequired
                        />

                        {/* Character Count with Min/Max */}
                        <Text fontSize="xs" color={secondaryText} textAlign="right" mt={1}>
                          {protestNote?.length || 0}/500 (minimum 20 characters)
                        </Text>

                        {/* Protest Button */}
                        <Flex justify="center" mt={3}>
                          <Button
                            bg="red.600"
                            color="white"
                            size="md"
                            onClick={handleProtestTrade}
                            isLoading={tradeState.isSubmitting}
                            isDisabled={!protestNote || protestNote.trim().length < 20 || protestNote.trim().length > 500}
                            width="200px"
                            _hover={{ bg: "red.700" }}
                            _active={{ bg: "red.800" }}
                            _disabled={{ bg: "gray.400", cursor: "not-allowed" }}
                          >
                            PROTEST TRADE
                          </Button>
                        </Flex>
                      </>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </ModalBody>

          <ModalFooter
            borderTop="1px solid"
            borderColor={borderColor}
            pt={4}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {/* Left side - Expiration countdown */}
            <Box flex="1">
              {tradeId && status === 'has_salary_issue' && (
                <Text
                  fontSize="sm"
                  color={secondaryText}
                  dangerouslySetInnerHTML={{ __html: getExpiryCountdown(expires) }}
                />
              )}
            </Box>

            {/* Center - Trade ID */}
            <Box flex="1" textAlign="center">
              {tradeId && (
                <Text fontSize="sm" color={secondaryText}>
                  Trade ID: {(tradeState.original || passedOriginal) ? `${tradeState.original || passedOriginal} (${tradeId})` : tradeId}
                </Text>
              )}
            </Box>

            {/* Right side - Action buttons - Only show if trade is not completed and not in special statuses */}
            <Box flex="1">
              {(() => {
                // Check if trade should be marked as expired
                const isExpired = (tradeState.dealStatus || status) !== 'under_review' &&
                  (tradeState.dealStatus || status) !== 'league_review' &&
                  (tradeState.expires || expires) &&
                  new Date(tradeState.expires || expires) < new Date();

                // Hide CTAs for completed trades, special statuses, review statuses, settlement statuses, expired trades, and canceled trades
                return !isTradeCompleted(tradeState.dealStatus || status) &&
                  !['has_salary_issue', 'completed', 'canceled'].includes(tradeState.dealStatus || status) &&
                  !(tradeState.dealStatus || status)?.toLowerCase().includes('review') &&
                  !(tradeState.dealStatus || status)?.toLowerCase().includes('settlement') &&
                  !isExpired;
              })() && (
                  <HStack spacing={3} justify="flex-end">
                    {tradeCTA2 && (
                      <Button
                        variant="solid"
                        onClick={handleTradeCTA2}
                        isLoading={tradeState.isSubmitting}
                        bg={tradeCTA2?.includes('Withdraw') ? 'orange.600' : 'red.700'}
                        color="white"
                        _hover={{
                          bg: tradeCTA2?.includes('Withdraw') ? 'orange.700' : 'red.800'
                        }}
                        isDisabled={(() => {
                          // Get current team IDs from state if props are not available
                          const currentTeam1Id = team1 || tradeState.team1Info?.id;
                          const currentTeam2Id = team2 || tradeState.team2Info?.id;

                          // For proposers: button is disabled if no changes or missing data
                          if (isProposer) {
                            if (hasPlayerChanges && tradeId) return true;
                            if (!currentTeam1Id || !currentTeam2Id) return true;
                            if (!tradeId && (tradeState.team1Players.length === 0 || tradeState.team2Players.length === 0)) return true;
                            return false;
                          }

                          // For non-proposers: Decline is disabled if player changes were made
                          if (!isProposer && tradeCTA2 === 'Decline' && hasPlayerChanges) return true;
                          return false;
                        })()}
                      >
                        {tradeCTA2}
                      </Button>
                    )}
                    <Button
                      colorScheme="blue"
                      onClick={handleSubmitTrade}
                      isLoading={tradeState.isSubmitting}
                      isDisabled={(() => {
                        // Get current team IDs from state if props are not available
                        const currentTeam1Id = team1 || tradeState.team1Info?.id;
                        const currentTeam2Id = team2 || tradeState.team2Info?.id;

                        // For proposers: button is disabled if no changes or missing data
                        if (isProposer) {
                          if (!hasPlayerChanges && !hasNoteChanges) return true;
                          if (!currentTeam1Id || !currentTeam2Id) return true;
                          if (!tradeId && (tradeState.team1Players.length === 0 || tradeState.team2Players.length === 0)) return true;
                          return false;
                        }

                        // For non-proposers: Agree is disabled if player changes were made
                        if (!isProposer && (tradeState.team1Players.length === 0 || tradeState.team2Players.length === 0)) return true;
                        return false;
                      })()}
                    >
                      {tradeCTA}
                    </Button>
                  </HStack>
                )}
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Player Selection Modal */}
      <PlayerSelectionModal
        isOpen={playerSelectionModal.isOpen}
        onClose={() => setPlayerSelectionModal({ isOpen: false, teamId: 0 })}
        teamId={playerSelectionModal.teamId}
        teamInfo={playerSelectionModal.teamInfo}
        onPlayerSelect={handlePlayerSelect}
        selectedPlayers={playerSelectionModal.teamId === (team1 || tradeState.team1Info?.id) ? tradeState.team1Players : tradeState.team2Players}
      />

      {/* Team Selection Modal */}
      {isTeamSelectionOpen && myLeague && myLeague.conferences && (
        <SelectTeam
          isOpen={isTeamSelectionOpen}
          onClose={() => setIsTeamSelectionOpen(false)}
          onSelect={handleTeamSelect}
          excludeMyTeam={true}
          league={myLeague}
          myTeam={team1 || tradeState.team1Info?.id}
        />
      )}

      {/* Generic Confirmation Dialog - Only render when needed */}
      {isAreYouSureOpen && (
        <AreYouSure
          isOpen={isAreYouSureOpen}
          onClose={() => setIsAreYouSureOpen(false)}
          onConfirm={() => {
            if (areYouSureFunction) {
              areYouSureFunction();
            }
            setIsAreYouSureOpen(false);
          }}
          message={areYouSureMessage}
          confirmText="Confirm"
          confirmColorScheme="red"
        />
      )}

      {/* Stripe Pre-Approval Modal */}
      <StripePaymentForm
        isOpen={isPreApprovalOpen}
        onClose={handlePreApprovalClose}
        amount={preApprovalAmount}
        payload={{
          type: 'trade_salary_cap_exceedance',
          tradeId: tradeId || undefined,
          dealId: tradeId || undefined, // Also include as dealId for consistency in metadata
          teamId: myTeam || undefined,
          leagueId: myLeague?.id || undefined,
        }}
        onSuccess={handlePreApprovalSuccess}
      />

    </>
  );
};

export default TradeModal; 